import React from 'react'
import './TermsPage.css'

function TermsPage() {
  return (
    <div className="terms-page">
      <div className="terms-container">
        <h1>NeighborTopia</h1>
        <h2>Terms of Service & Privacy Policy</h2>
        <p className="effective-date">Effective Date: January 22, 2026</p>

        <p>Welcome to NeighborTopia! These Terms of Service ("Terms") and Privacy Policy govern your use of the NeighborTopia mobile application, web application, and related services (collectively, the "Service") operated by NeighborTopia ("we," "us," or "our").</p>
        
        <p>By accessing or using our Service, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you may not access the Service.</p>

        <h2 className="part-title">PART 1: TERMS OF SERVICE</h2>

        <h3>1. Account Registration and Eligibility</h3>
        
        <h4>1.1 Eligibility</h4>
        <p>To use NeighborTopia, you must:</p>
        <ul>
          <li>Be at least 18 years of age or the age of majority in your jurisdiction</li>
          <li>Be a resident or have a legitimate connection to a registered neighborhood community</li>
          <li>Have received a valid invitation code from an existing community member or administrator</li>
          <li>Provide accurate and complete registration information</li>
        </ul>

        <h4>1.2 Account Security</h4>
        <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to immediately notify us of any unauthorized use of your account or any other breach of security.</p>

        <h4>1.3 Account Accuracy</h4>
        <p>You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete. We reserve the right to suspend or terminate accounts that contain inaccurate or fraudulent information.</p>

        <h3>2. Household Management</h3>

        <h4>2.1 Household Accounts</h4>
        <p>NeighborTopia allows households to be associated with a single residential address. Each household may include up to five (5) members, including a primary resident and secondary residents (such as family members, roommates, or tenants).</p>

        <h4>2.2 Invitations</h4>
        <p>Primary residents may invite additional household members using our invitation system. By inviting someone to join your household, you represent that the invitee has a legitimate connection to your residence and that you have their permission to share their contact information with us for invitation purposes.</p>

        <h4>2.3 Responsibility</h4>
        <p>Primary residents are responsible for managing household member access and ensuring all household members comply with these Terms.</p>

        <h3>3. Community Guidelines and Acceptable Use</h3>

        <h4>3.1 Expected Conduct</h4>
        <p>NeighborTopia is designed to foster positive community engagement. When using our Service, you agree to:</p>
        <ul>
          <li>Treat other community members with respect and courtesy</li>
          <li>Post only truthful and accurate information</li>
          <li>Respect the privacy of other residents</li>
          <li>Use the Service only for its intended neighborhood community purposes</li>
          <li>Comply with all applicable local, state, and federal laws</li>
        </ul>

        <h4>3.2 Prohibited Conduct</h4>
        <p>You agree NOT to:</p>
        <ul>
          <li>Harass, bully, threaten, or intimidate other users</li>
          <li>Post content that is defamatory, obscene, hateful, or discriminatory</li>
          <li>Share another person's personal information without their consent</li>
          <li>Use the Service for commercial advertising or solicitation without authorization</li>
          <li>Attempt to gain unauthorized access to other accounts or our systems</li>
          <li>Upload malware, viruses, or other harmful code</li>
          <li>Impersonate another person or misrepresent your affiliation</li>
          <li>Use the Service to violate any applicable law or regulation</li>
          <li>Interfere with or disrupt the Service or servers connected to the Service</li>
        </ul>

        <h3>4. User Content</h3>

        <h4>4.1 Ownership</h4>
        <p>You retain ownership of any content you submit, post, or display on or through the Service ("User Content"). By posting User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, copy, modify, and display such content solely for the purpose of operating and providing the Service.</p>

        <h4>4.2 Content Responsibility</h4>
        <p>You are solely responsible for your User Content. We do not endorse, support, or guarantee the accuracy of any User Content. We reserve the right to remove any User Content that violates these Terms or that we find objectionable, without prior notice.</p>

        <h4>4.3 Content Guidelines</h4>
        <p>User Content must not contain material that infringes any third-party intellectual property rights, contains personal information of others without consent, or violates any applicable law.</p>

        <h3>5. Subscriptions and Payments</h3>

        <h4>5.1 Subscription Plans</h4>
        <p>Certain features of NeighborTopia may require a paid subscription. Subscription plans, pricing, and features are described on our website and within the application. We reserve the right to modify subscription plans and pricing at any time, with notice to current subscribers.</p>

        <h4>5.2 Billing</h4>
        <p>Subscription fees are billed in advance on a monthly or annual basis, depending on your selected plan. Payment is processed through our third-party payment processor, Stripe. By subscribing, you authorize us to charge your designated payment method for recurring subscription fees.</p>

        <h4>5.3 Free Trials</h4>
        <p>We may offer free trial periods for new subscribers. At the end of the trial period, your subscription will automatically convert to a paid subscription unless you cancel before the trial ends.</p>

        <h4>5.4 Cancellation and Refunds</h4>
        <p>You may cancel your subscription at any time through your account settings or by contacting us. Cancellation will take effect at the end of your current billing period. We generally do not provide refunds for partial subscription periods, except as required by applicable law.</p>

        <h3>6. Intellectual Property</h3>
        <p>The Service and its original content (excluding User Content), features, and functionality are and will remain the exclusive property of NeighborTopia and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used without our prior written consent.</p>

        <h3>7. Disclaimers</h3>
        <p>THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. WE MAKE NO WARRANTIES, EXPRESS OR IMPLIED, REGARDING THE SERVICE, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
        <p>We do not warrant that the Service will be uninterrupted, secure, or error-free; that any defects will be corrected; or that the Service is free of viruses or other harmful components.</p>
        <p>NeighborTopia is not affiliated with any homeowners association, property management company, or governmental entity unless explicitly stated. We are not responsible for enforcing HOA rules or community regulations.</p>

        <h3>8. Limitation of Liability</h3>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEIGHBORTOPIA AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:</p>
        <ul>
          <li>Your use or inability to use the Service</li>
          <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
          <li>Any conduct or content of any third party on the Service</li>
          <li>Any content obtained from the Service</li>
        </ul>
        <p>IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE AMOUNT YOU HAVE PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.</p>

        <h3>9. Indemnification</h3>
        <p>You agree to defend, indemnify, and hold harmless NeighborTopia and its officers, directors, employees, contractors, agents, licensors, and suppliers from and against any claims, liabilities, damages, judgments, awards, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Service.</p>

        <h3>10. Termination</h3>
        <p>We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Service will immediately cease.</p>
        <p>You may terminate your account at any time by contacting us or using the account deletion feature in the Service. Upon account deletion, we will delete your personal information in accordance with our Privacy Policy, except as required to comply with legal obligations.</p>

        <h3>11. Governing Law and Dispute Resolution</h3>
        <p>These Terms shall be governed by and construed in accordance with the laws of the State of Nebraska, United States, without regard to its conflict of law provisions.</p>
        <p>Any dispute arising from or relating to these Terms or the Service shall first be attempted to be resolved through good-faith negotiations. If the dispute cannot be resolved through negotiations within thirty (30) days, either party may pursue resolution through binding arbitration or in the courts located in Douglas County, Nebraska.</p>

        <h3>12. Changes to Terms</h3>
        <p>We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least thirty (30) days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>
        <p>By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised Terms.</p>

        <h2 className="part-title">PART 2: PRIVACY POLICY</h2>
        <p>This Privacy Policy describes how NeighborTopia collects, uses, and shares your personal information when you use our Service.</p>

        <h3>1. Information We Collect</h3>

        <h4>1.1 Information You Provide</h4>
        <p>We collect information you provide directly to us, including:</p>
        <ul>
          <li>Account information: name, email address, password, phone number</li>
          <li>Profile information: profile photo, occupation, address</li>
          <li>Content you create: posts, comments, announcements, messages</li>
          <li>Payment information: billing address, payment method details (processed by Stripe)</li>
          <li>Communications: emails, support requests, feedback</li>
        </ul>

        <h4>1.2 Information Collected Automatically</h4>
        <p>When you use our Service, we automatically collect certain information, including:</p>
        <ul>
          <li>Device information: device type, operating system, unique device identifiers</li>
          <li>Log information: access times, pages viewed, IP address, referring URL</li>
          <li>Usage information: features used, actions taken within the app</li>
        </ul>

        <h4>1.3 Information from Third Parties</h4>
        <p>We may receive information about you from other sources, including from other users who invite you to join a household or community, and from third-party services you connect to your account.</p>

        <h3>2. How We Use Your Information</h3>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve our Service</li>
          <li>Create and manage your account</li>
          <li>Process transactions and send related information</li>
          <li>Send you technical notices, updates, security alerts, and support messages</li>
          <li>Respond to your comments, questions, and requests</li>
          <li>Facilitate community features such as discussions and announcements</li>
          <li>Monitor and analyze trends, usage, and activities</li>
          <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
          <li>Personalize and improve your experience</li>
        </ul>

        <h3>3. How We Share Your Information</h3>

        <h4>3.1 With Your Community</h4>
        <p>Your profile information and content may be visible to other members of your neighborhood community based on your privacy settings. You can control what information is displayed in the community directory through your privacy settings.</p>

        <h4>3.2 With Service Providers</h4>
        <p>We share information with third-party service providers that perform services on our behalf, including:</p>
        <ul>
          <li>Firebase (Google): Authentication, database, and hosting services</li>
          <li>Stripe: Payment processing</li>
          <li>Amazon Web Services (SES): Email delivery</li>
        </ul>

        <h4>3.3 For Legal Reasons</h4>
        <p>We may disclose your information if required by law, legal process, or government request; to enforce our Terms; to protect the rights, privacy, safety, or property of NeighborTopia, our users, or the public; or to detect, prevent, or address fraud, security, or technical issues.</p>

        <h4>3.4 Business Transfers</h4>
        <p>If NeighborTopia is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change and any choices you may have regarding your information.</p>

        <h4>3.5 No Sale of Personal Information</h4>
        <p>We do not sell your personal information to third parties for their marketing purposes.</p>

        <h3>4. Data Retention</h3>
        <p>We retain your personal information for as long as your account is active or as needed to provide you services. We will also retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements.</p>
        <p>When you delete your account, we will delete your personal information within thirty (30) days, except for information we are required to retain for legal, regulatory, or security purposes.</p>

        <h3>5. Data Security</h3>
        <p>We implement appropriate technical and organizational security measures designed to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure data storage, and access controls.</p>
        <p>However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.</p>

        <h3>6. Your Rights and Choices</h3>

        <h4>6.1 Access and Update</h4>
        <p>You can access and update your account information at any time through your profile settings in the app.</p>

        <h4>6.2 Privacy Controls</h4>
        <p>You can control your privacy settings, including whether your email, phone number, and profile appear in the community directory.</p>

        <h4>6.3 Account Deletion</h4>
        <p>You may request deletion of your account and personal data by contacting us at info@neighbortopia.app. We will process your request within thirty (30) days.</p>

        <h4>6.4 Email Communications</h4>
        <p>You may opt out of promotional emails by following the unsubscribe instructions in those emails. You cannot opt out of transactional emails related to your account, such as password resets and security notices.</p>

        <h4>6.5 Data Portability</h4>
        <p>Upon request, we will provide you with a copy of your personal data in a commonly used, machine-readable format.</p>

        <h3>7. Children's Privacy</h3>
        <p>Our Service is not intended for children under the age of 13 (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us and we will delete such information.</p>

        <h3>8. International Data Transfers</h3>
        <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country. We take appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy.</p>

        <h3>9. California Privacy Rights</h3>
        <p>If you are a California resident, you have certain rights under the California Consumer Privacy Act (CCPA), including:</p>
        <ul>
          <li>The right to know what personal information we collect, use, and disclose</li>
          <li>The right to request deletion of your personal information</li>
          <li>The right to opt out of the sale of your personal information (we do not sell personal information)</li>
          <li>The right to non-discrimination for exercising your privacy rights</li>
        </ul>
        <p>To exercise these rights, please contact us at info@neighbortopia.app.</p>

        <h3>10. Cookies and Similar Technologies</h3>
        <p>We use cookies and similar tracking technologies to collect and store information when you use our Service. Cookies are small data files stored on your device. We use:</p>
        <ul>
          <li>Essential cookies: Required for the Service to function properly</li>
          <li>Analytics cookies: Help us understand how you use the Service</li>
          <li>Preference cookies: Remember your settings and preferences</li>
        </ul>
        <p>You can control cookies through your browser settings. Disabling certain cookies may limit your ability to use some features of our Service.</p>

        <h3>11. Changes to This Privacy Policy</h3>
        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top. For material changes, we will provide notice through the Service or by email.</p>

        <h2 className="part-title">Contact Us</h2>
        <p>If you have any questions about these Terms or our Privacy Policy, please contact us:</p>
        <p><strong>NeighborTopia</strong></p>
        <p>Email: <a href="mailto:info@neighbortopia.app">info@neighbortopia.app</a></p>
        <p>Website: <a href="https://neighbortopia-app.web.app">https://neighbortopia-app.web.app</a></p>

        <p className="copyright">© 2026 NeighborTopia. All rights reserved.</p>
      </div>
    </div>
  )
}

export default TermsPage